import {Close as CloseIcon} from '@mui/icons-material';
import Popover from '@mui/material/Popover';
import {styled} from '@mui/material/styles';
import clsx from 'clsx';
import React, {useState} from 'react';
import {Box} from '../../Box';
import {IconButton} from '../../Button';
import {Typography} from '../../Typography';
import {composeEventHandlers} from '../../utils/composeEventHandlers';
import {useFileInputContext} from '../FileInputContext';
import {Validation} from '../types/Validations';
import {getFileIcon} from '../utils/getFileIcon';
import {getFileStatusIcon} from '../utils/getFileStatusIcon';

type BaseFileType = Partial<File>;
type BaseLiProps = React.HTMLAttributes<HTMLLIElement>;

// type FileListItemStatus = 'validating' | FuelFeedbackType;

export interface FileListItemProps extends BaseFileType, BaseLiProps {
    onClose?: () => void;
    status?: Validation['status'];
    checks?: Validation['checks'];
}

const StyledListItem = styled('li')(({theme: {tokens}}) => {
    return {
        listStyle: 'none',
        display: 'flex',
        alignItems: 'center',

        '& .FileListItem-child': {
            // lineHeight: localTokens.default.itemHeight,
            marginLeft: tokens.FuelSpacing1,
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',

            '&.FileListItem-icon': {
                marginLeft: 0,
                width: tokens.FuelSpacing6,
                height: tokens.FuelSpacing6,
                display: ''
            },

            '&.FileListItem-text': {
                flex: 1,
                justifyContent: 'flex-start',
                display: 'block'
            },

            '&.FileListItem-status': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: tokens.FuelSpacing6,
                width: tokens.FuelSpacing6
            },

            '&.FileListItem-close': {
                height: tokens.FuelSpacing6,
                width: tokens.FuelSpacing6
            }
        }
    };
});

export const FileListItem = React.forwardRef<HTMLLIElement, FileListItemProps>(({
    name, onClick, onClose, className, status, checks
}, ref) => {
    const {
        textMap, validators
    } = useFileInputContext();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const {
        icon: IconComponent, color
    } = getFileIcon(name);

    const handleOpenPopover: React.MouseEventHandler = (event) => {
        setAnchorEl(event.target as HTMLElement);
    };

    const handleClosePopover = (): void => {
        setAnchorEl(null);
    };

    const handleDelete = composeEventHandlers(() => {
        if (typeof onClose === 'function') {
            onClose();
        }
    }, onClick);

    return (
        <StyledListItem
            className={clsx('FileListItem-root', className)}
            ref={ref}
        >
            <IconComponent
                className='FileListItem-child FileListItem-icon'
                fontSize='small'
                sx={{
                    color
                }}
            />
            <Typography
                className='FileListItem-child FileListItem-text'
                display='block'
                noWrap
            >
                {name}
            </Typography>
            <IconButton
                aria-label={`${status}`}
                className='FileListItem-child FileListItem-status'
                component={IconButton}
                size='small'
                {...validators.length > 0 && {
                    onMouseEnter: handleOpenPopover,
                    onMouseLeave: handleClosePopover
                }}
            >
                {getFileStatusIcon(status || 'validating', textMap)}
            </IconButton>
            {validators.length > 0 && (
                <Popover
                    PaperProps={{
                        sx: (theme): any => ({
                            borderRadius: theme.tokens.FuelBorderRadiusLg,
                            boxShadow: theme.tokens.FuelShadowMd
                        })
                    }}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                    }}
                    onClose={handleClosePopover}
                    open={Boolean(anchorEl)}
                    sx={{
                        pointerEvents: 'none'
                    }}
                    transformOrigin={{
                        horizontal: 'center',
                        vertical: 'bottom'
                    }}
                >
                    <Box minWidth={250}>
                        <Box
                            bgcolor='grey.200'
                            padding='0.5rem 0.75rem'
                        >
                            <Typography variant='subtitle2'>
                                {`${textMap.validationTitle} (`}
                                {checks ? Object.keys(checks).length : ''}
                                {')'}
                            </Typography>
                        </Box>
                        {checks &&
                        Object.keys(checks).map((checkKey) => (
                            <Box
                                alignItems='center'
                                bgcolor={
                                    checks[checkKey] ? '#F4F8F4' : '#FDF6F6'
                                }
                                borderTop='solid 1px #00000020'
                                display='flex'
                                key={checkKey}
                                padding='0.5rem 0.75rem'
                            >
                                <Typography
                                    display='block'
                                    flexGrow={1}
                                    noWrap
                                    variant='body2'
                                >
                                    {checkKey}
                                </Typography>
                                {getFileStatusIcon(
                                    checks[checkKey] ? 'success' : 'error',
                                    textMap
                                )}
                            </Box>
                        ))
                        }
                    </Box>
                </Popover>
            )}
            <IconButton
                aria-label={textMap.listItemRemove}
                className='FileListItem-child FileListItem-close'
                component='div'
                onClick={handleDelete}
                size='small'
            >
                <CloseIcon/>
            </IconButton>
        </StyledListItem>
    );
});

FileListItem.displayName = 'FileListItem';
